/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useVideoControl } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import {
  EventWarning,
  ScrapEyesEvent,
} from "components/scrapEyeLabelling/types";
import { useAreaLabelledEvent } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNotifyStatus } from "src/contexts/status";
import { sorted } from "src/helpers";
import { v4 as uuidv4 } from "uuid";
import { findNearestCandidate } from "../utils/findNearestCandidate";
import { EventCreate, EventType } from "src/store/api/generatedApi";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { useAreaMaterialMapping } from "./useAreaMaterialMapping";

dayjs.extend(isSameOrBefore);

// TODO: Get this from the metadata
const CANDIDATE_GAP_THRESHOLD_SECONDS = 10;

/** The candidate close enough to supply this event's mass, if any. */
const nearestUsableCandidate = (
  area: string | null,
  eventStart: dayjs.Dayjs,
  eventEnd: dayjs.Dayjs,
  candidateEvents: EventCreate[],
  areaMatching: boolean
): EventCreate | null => {
  const matchingCandidates = areaMatching
    ? candidateEvents.filter((candidate) => {
        const areaLabel = candidate.labels.find((l) => l.label_type === "area");
        return areaLabel?.label === area;
      })
    : candidateEvents;

  const nearestCandidate = findNearestCandidate(matchingCandidates, eventStart);

  if (!nearestCandidate) {
    return null;
  }

  const candidateStart = dayjs(nearestCandidate.event.start);
  const candidateEnd = dayjs(nearestCandidate.event.end);

  const hasOverlap =
    eventStart.isBefore(candidateEnd) && eventEnd.isAfter(candidateStart);
  const gapSeconds = hasOverlap
    ? 0
    : Math.min(
        Math.abs(eventStart.diff(candidateEnd, "second")),
        Math.abs(eventEnd.diff(candidateStart, "second"))
      );

  return hasOverlap || gapSeconds < CANDIDATE_GAP_THRESHOLD_SECONDS
    ? nearestCandidate
    : null;
};

const calculateMassAndWarnings = (
  area: string | null,
  eventStart: dayjs.Dayjs,
  eventEnd: dayjs.Dayjs,
  candidateEvents: EventCreate[],
  areaMatching: boolean
): { mass: string | null; warnings: EventWarning[] } => {
  if (areaMatching && !area) {
    return { mass: null, warnings: [] };
  }
  const candidate = nearestUsableCandidate(
    area,
    eventStart,
    eventEnd,
    candidateEvents,
    areaMatching
  );
  if (!candidate) {
    return { mass: "0", warnings: ["noCandidateEventFound"] };
  }
  const massLabel = candidate.labels.find((l) => l.label_type === "mass");
  return { mass: massLabel?.label ?? null, warnings: [] };
};

/** The mass a drop carries, or null when it carries none we can use. */
const massWeight = (mass: string | null | undefined): number | null => {
  if (mass == null) return null;
  const weight = Number(mass);
  return Number.isFinite(weight) && weight > 0 ? weight : null;
};

/** The event moved to `area`. Its scale levels were read off the old area's
 * scale, so they do not move with the event. */
const inArea = <T extends ScrapEyesEvent>(event: T, area: string | null): T =>
  area === event.area
    ? event
    : { ...event, area, initialMass: null, finalMass: null };

interface CandidateWeightTotals {
  knownWeightSum: number;
  knownCount: number;
  unknownCount: number;
}

/** The share formula from `_candidate_share` in `scrapeye_identifiers.mass_pairing`:
 * a drop with no usable mass weighs as a typical competitor, the mean of the knowns.
 * Null where the backend keeps the whole candidate, which here means leaving the
 * event's own un-apportioned suggestion alone. */
const candidateShare = (
  totals: CandidateWeightTotals,
  ownWeight: number | null
): number | null => {
  const meanKnownWeight =
    totals.knownCount > 0 ? totals.knownWeightSum / totals.knownCount : 1;
  const effectiveTotal =
    totals.knownWeightSum + totals.unknownCount * meanKnownWeight;
  if (effectiveTotal <= 0) return null;
  return Math.min((ownWeight ?? meanKnownWeight) / effectiveTotal, 1);
};

const candidateClaimKey = (candidate: EventCreate): string =>
  [
    candidate.event.start,
    candidate.labels.find((l) => l.label_type === "area")?.label ?? "",
    candidate.event.event_type,
  ].join(" ");

const applyDropMassShares = <T extends ScrapEyesEvent>(
  events: T[],
  candidateEvents: EventCreate[] | undefined,
  massCandidateSharing: string,
  massAreaMatching: boolean,
  hasMassData: boolean
): T[] => {
  if (
    massCandidateSharing !== "shared" ||
    !hasMassData ||
    !candidateEvents ||
    candidateEvents.length === 0
  ) {
    return events;
  }

  const sourceByEventId = new Map<string, EventCreate>();
  const totalsByClaimKey = new Map<string, CandidateWeightTotals>();
  for (const event of events) {
    if (event.eventType !== "drop") continue;
    if (massAreaMatching && !event.area) continue;
    const candidate = nearestUsableCandidate(
      event.area ?? null,
      event.start,
      event.end,
      candidateEvents,
      massAreaMatching
    );
    if (!candidate) continue;
    sourceByEventId.set(event.id, candidate);
    const key = candidateClaimKey(candidate);
    const totals = totalsByClaimKey.get(key) ?? {
      knownWeightSum: 0,
      knownCount: 0,
      unknownCount: 0,
    };
    const weight = massWeight(event.mass);
    totalsByClaimKey.set(
      key,
      weight === null
        ? { ...totals, unknownCount: totals.unknownCount + 1 }
        : {
            ...totals,
            knownWeightSum: totals.knownWeightSum + weight,
            knownCount: totals.knownCount + 1,
          }
    );
  }

  return events.map((event) => {
    const candidate = sourceByEventId.get(event.id);
    if (!candidate) return event;
    const totals = totalsByClaimKey.get(candidateClaimKey(candidate));
    const candidateMass = Number(
      candidate.labels.find((l) => l.label_type === "mass")?.label
    );
    if (!totals || !Number.isFinite(candidateMass)) return event;
    const share = candidateShare(totals, massWeight(event.mass));
    if (share === null) return event;
    return { ...event, mass: String(candidateMass * share) };
  });
};

const applyLiftMassSuggestions = <T extends ScrapEyesEvent>(
  events: T[],
  candidateEvents: EventCreate[] | undefined,
  massEventMatching: string,
  massAreaMatching: boolean,
  hasMassData: boolean
): T[] => {
  if (
    massEventMatching !== "on_lift" ||
    !hasMassData ||
    !candidateEvents ||
    candidateEvents.length === 0
  ) {
    return events;
  }
  const sortedEvents = sorted(events, (event) => event.start.valueOf());
  const liftMassById = new Map<
    string,
    { mass: string | null; warnings: EventWarning[] }
  >();

  let nextLiftOrDropStart: dayjs.Dayjs | null = null;
  for (let i = sortedEvents.length - 1; i >= 0; i--) {
    const event = sortedEvents[i];
    if (!event) continue;

    if (event.eventType === "lift") {
      const windowStart = event.start;
      const windowEnd = nextLiftOrDropStart;

      if (massAreaMatching && !event.area) {
        liftMassById.set(event.id, { mass: null, warnings: [] });
      } else {
        let latest: EventCreate | null = null;
        for (const candidate of candidateEvents) {
          if (candidate.event.event_type !== "lift") continue;
          const candidateStart = dayjs(candidate.event.start);
          if (candidateStart.isBefore(windowStart)) continue;
          if (windowEnd !== null && !candidateStart.isBefore(windowEnd))
            continue;
          if (massAreaMatching) {
            const areaLabel = candidate.labels.find(
              (l) => l.label_type === "area"
            );
            if (areaLabel?.label !== event.area) continue;
          }
          if (
            latest === null ||
            candidateStart.isAfter(dayjs(latest.event.start))
          ) {
            latest = candidate;
          }
        }

        if (latest === null) {
          liftMassById.set(event.id, {
            mass: "0",
            warnings: ["noCandidateEventFound"],
          });
        } else {
          const massLabel = latest.labels.find((l) => l.label_type === "mass");
          liftMassById.set(event.id, {
            mass: massLabel?.label ?? null,
            warnings: [],
          });
        }
      }
    }

    if (event.eventType === "lift" || event.eventType === "drop") {
      nextLiftOrDropStart = event.start;
    }
  }

  return events.map((event) => {
    const value = liftMassById.get(event.id);
    if (!value) return event;
    return {
      ...event,
      mass: value.mass,
      warnings: value.warnings.length > 0 ? value.warnings : undefined,
    };
  });
};

type PreRecordingState = Readonly<{
  wasPlaying: boolean;
  speed: number;
}>;

type PendingMaterial = Readonly<{
  material: string | null;
  timestamp: number;
  appliedToEventId: string | null;
  previousMaterial: string | null;
}>;

type State = Readonly<{
  selectedEventId: string | null;
  recordingEventId: string | null;
  pendingMaterial: PendingMaterial | null;
}>;

type Action<T extends ScrapEyesEvent> =
  | { type: "SELECT_EVENT"; eventId: string | null }
  | {
      type: "START_RECORDING";
      event: T;
    }
  | { type: "STOP_RECORDING" }
  | { type: "CLEAR_SELECTION" };

const initialState: State = {
  selectedEventId: null,
  recordingEventId: null,
  pendingMaterial: null,
};

const reducer = <T extends ScrapEyesEvent>(
  state: State,
  action: Action<T>
): State => {
  switch (action.type) {
    case "SELECT_EVENT":
      return { ...state, selectedEventId: action.eventId };
    case "START_RECORDING":
      return {
        ...state,
        selectedEventId: action.event.id,
        recordingEventId: action.event.id,
      };
    case "STOP_RECORDING":
      return {
        ...state,
        recordingEventId: null,
      };
    case "CLEAR_SELECTION":
      return { ...state, selectedEventId: null };
    default:
      return state;
  }
};

const breakingChangesVersion = 2;

type SerializableEvent = Omit<ScrapEyesEvent, "start" | "end"> & {
  start: string;
  end: string;
};

const serializeEvents = (events: ScrapEyesEvent[]): SerializableEvent[] => {
  return events.map((event) => {
    // Strip out heavy fields that we don't need to persist
    const { id, eventType, material, comment, area, start, end, ...rest } =
      event;
    return {
      ...rest,
      id,
      eventType,
      material,
      comment,
      area,
      start: start.toISOString(),
      end: end.toISOString(),
    };
  });
};

const deserializeEvents = <T extends ScrapEyesEvent>(
  serializedEvents: SerializableEvent[],
  defaultConstructor: (event: ScrapEyesEvent) => T
): T[] => {
  return serializedEvents.map((event) =>
    defaultConstructor({
      ...event,
      start: dayjs(event.start),
      end: dayjs(event.end),
    })
  );
};

// Patch update, undefined means don't touch. "null" means clear value.
export type UpdateBody<T extends ScrapEyesEvent> = {
  start?: dayjs.Dayjs;
  end?: dayjs.Dayjs;
  comment?: string | null;
  material?: string | null;
  coMaterial?: string | null;
  area?: string | null;
  mass?: string | null;
  initialMass?: number | null;
  finalMass?: number | null;
} & Partial<Omit<T, keyof ScrapEyesEvent>>;

export type MaterialEditScope = "withConnected" | "eventOnly";

const findPairPartnerIds = (
  events: ScrapEyesEvent[],
  eventId: string
): string[] => {
  const pairId = events.find((event) => event.id === eventId)?.pairId;
  return pairId
    ? events
        .filter((event) => event.pairId === pairId && event.id !== eventId)
        .map((event) => event.id)
    : [];
};

export const useEventManagement = <T extends ScrapEyesEvent>(
  storageKey: string | null,
  videoStart: dayjs.Dayjs,
  defaultConstructor: (event: ScrapEyesEvent) => T,
  // How a paired lift and drop present their material.
  // "shared": the drop's displayed material is always taken from its lift — a
  //   basket is labelled once, so the pair always reads as one material
  //   (labelling).
  // "independent": the drop keeps its own labelled material, so a disagreement
  //   between two labelled events is surfaced, not hidden (review). A drop with
  //   no material of its own still inherits the lift's, so a newly-recorded drop
  //   matches its lift.
  pairedMaterialDisplay: "shared" | "independent",
  candidateEvents?: EventCreate[],
  locationKey?: string | null,
  lastPeriodEnd?: dayjs.Dayjs,
  labelRecordingPlaybackRate?: number,
  firstPeriodStart?: dayjs.Dayjs,
  areaMaterialStorageKey?: string | null,
  findConnectedEventIds: (
    events: T[],
    eventId: string
  ) => string[] = findPairPartnerIds
) => {
  const shouldStoreLocally = storageKey !== null;

  const {
    default_areas: defaultAreas,
    extra_data: extraData,
    default_area_materials: defaultAreaMaterials,
    mass_area_matching: massAreaMatching,
    mass_event_matching: massEventMatching,
    label_review_config: labelReviewConfig,
    label_recording_playback_rate: tenantLabelRecordingPlaybackRate,
  } = useScrapEyeMetadata();

  const effectiveLabelRecordingPlaybackRate =
    labelRecordingPlaybackRate ?? tenantLabelRecordingPlaybackRate;

  const {
    state: { currentTime, isPlaying, playbackRate, duration },
    actions: { seekTo, play, pause, setPlaybackRate, getLiveCurrentTime },
  } = useVideoControl();

  const effectiveVideoEnd = useMemo(
    () => lastPeriodEnd ?? videoStart.add(duration, "second"),
    [lastPeriodEnd, videoStart, duration]
  );

  // videoStart itself stays the player's clock origin for converting
  // player-relative time to absolute timestamps; this is a separate,
  // possibly-narrower lower bound for a caller whose displayed window is
  // wider than the region it's safe to write into.
  const effectiveVideoStartForWrites = useMemo(
    () => firstPeriodStart ?? videoStart,
    [firstPeriodStart, videoStart]
  );

  const notifyStatus = useNotifyStatus();
  const { t } = useTranslation();

  const storageKeyWithVersion = `${
    storageKey ?? ""
  }-v${breakingChangesVersion}`;
  const [serializedEvents, setSerializedEvents] = useLocalStorageState<
    SerializableEvent[]
  >(storageKeyWithVersion, []);

  const [inMemoryEvents, setInMemoryEvents] = useState<T[]>([]);

  const {
    areaMaterialSnapshots,
    getAreaMaterialMappingAtTime,
    getAreaMaterialMappingBeforeTime,
    updateAreaMaterialMapping,
    deleteAreaMaterialSnapshot,
    isUsingPersistentDefaults,
    commitPersistentMapping,
  } = useAreaMaterialMapping(
    areaMaterialStorageKey === undefined ? storageKey : areaMaterialStorageKey,
    defaultAreaMaterials,
    locationKey
  );
  const withAreaLabels = useAreaLabelledEvent(locationKey ?? undefined);

  const events = useMemo(() => {
    if (!shouldStoreLocally) {
      return inMemoryEvents;
    }
    return deserializeEvents(serializedEvents, defaultConstructor);
  }, [
    shouldStoreLocally,
    inMemoryEvents,
    serializedEvents,
    defaultConstructor,
  ]);

  const setEvents = useCallback(
    (update: T[] | ((prev: T[]) => T[])) => {
      if (!shouldStoreLocally) {
        setInMemoryEvents((prev) =>
          typeof update === "function" ? update(prev) : update
        );
        return;
      }

      setSerializedEvents((prevSerialized) => {
        const prevEvents = deserializeEvents(
          prevSerialized,
          defaultConstructor
        );
        const nextEvents =
          typeof update === "function" ? update(prevEvents) : update;
        return serializeEvents(nextEvents);
      });
    },
    [
      shouldStoreLocally,
      setInMemoryEvents,
      setSerializedEvents,
      defaultConstructor,
    ]
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const creationPendingRef = useRef(false);
  const activeRecordingRef = useRef<{
    event: T;
    preRecordingState: PreRecordingState;
  } | null>(null);

  useEffect(() => {
    creationPendingRef.current = false;
  }, [events]);

  const eventsWithPairing = useMemo(() => {
    const eventsBeforePairing = sorted(
      events.map((event) => withAreaLabels(event)),
      (e) => e.start.valueOf()
    );
    const sortedEvents = [...eventsBeforePairing];
    const result: T[] = [];

    for (let i = 0; i < sortedEvents.length; i++) {
      const event = sortedEvents[i];
      if (!event) continue;

      if (event.eventType === "lift") {
        const nextDrop = sortedEvents
          .slice(i + 1)
          .find((e) => e.eventType === "drop");

        if (nextDrop) {
          const pairId = uuidv4();
          result.push({ ...event, pairId });
          const dropIndex = sortedEvents.findIndex((e) => e.id === nextDrop.id);
          if (dropIndex !== -1) {
            // In a double lift the drop is offered to each lift in turn, so
            // judge its own material, not one borrowed from an earlier lift.
            const dropInheritsLiftMaterial =
              pairedMaterialDisplay === "shared" ||
              eventsBeforePairing[dropIndex]?.material == null;
            sortedEvents[dropIndex] = dropInheritsLiftMaterial
              ? {
                  ...nextDrop,
                  pairId,
                  material: event.material,
                  coMaterial: event.coMaterial,
                }
              : { ...nextDrop, pairId };
          }
        } else {
          result.push(event);
        }
      } else if (
        event.pairId != null ||
        !result.some((e) => e.id === event.id)
      ) {
        result.push(event);
      }
    }

    return result;
  }, [events, pairedMaterialDisplay, withAreaLabels]);

  const eventsWithMassSuggestions = useMemo(
    () =>
      applyDropMassShares(
        applyLiftMassSuggestions(
          eventsWithPairing,
          candidateEvents,
          massEventMatching,
          massAreaMatching,
          extraData?.includes("mass_data") ?? false
        ),
        candidateEvents,
        labelReviewConfig?.mass_candidate_sharing ?? "exclusive",
        massAreaMatching,
        extraData?.includes("mass_data") ?? false
      ),
    [
      eventsWithPairing,
      candidateEvents,
      massEventMatching,
      massAreaMatching,
      labelReviewConfig,
      extraData,
    ]
  );

  const previousSelectionTimeRef = useRef<number | null>(null);
  useEffect(() => {
    const currentTimestamp = videoStart.add(currentTime, "second");
    const playheadMoved =
      previousSelectionTimeRef.current !== currentTimestamp.valueOf();
    previousSelectionTimeRef.current = currentTimestamp.valueOf();
    if (state.recordingEventId) return;
    if (
      !playheadMoved &&
      eventsWithPairing.some((event) => event.id === state.selectedEventId)
    )
      return;

    let closestEvent = null;
    for (let i = eventsWithPairing.length - 1; i >= 0; i--) {
      const candidate = eventsWithPairing[i];
      if (candidate?.start.isSameOrBefore(currentTimestamp)) {
        closestEvent = candidate;
        break;
      }
    }
    const newEventId = closestEvent?.id ?? null;
    if (state.selectedEventId !== newEventId) {
      dispatch({ type: "SELECT_EVENT", eventId: newEventId });
    }
  }, [
    currentTime,
    eventsWithPairing,
    state.recordingEventId,
    state.selectedEventId,
    videoStart,
  ]);

  const setSelectedEventId = useCallback(
    (id: string | null, selectionTimestamp?: dayjs.Dayjs) => {
      if (selectionTimestamp !== undefined)
        previousSelectionTimeRef.current = selectionTimestamp.valueOf();
      dispatch({ type: "SELECT_EVENT", eventId: id });
    },
    []
  );

  const handleSelectEvent = useCallback(
    (id: string | null) => {
      const event = eventsWithPairing.find((event) => event.id === id);
      const timeInSeconds = event ? event.start.diff(videoStart, "second") : 0;
      seekTo(timeInSeconds + 0.5);
      setSelectedEventId(id, videoStart.add(timeInSeconds + 0.5, "second"));
    },
    [eventsWithPairing, seekTo, videoStart, setSelectedEventId]
  );

  const handleDeleteEvent = useCallback(
    (id: string) => {
      setEvents((prev) => prev.filter((event) => event.id !== id));
      if (state.selectedEventId === id) {
        dispatch({ type: "CLEAR_SELECTION" });
      }
    },
    [state.selectedEventId, setEvents]
  );

  const constrainStart = useCallback(
    (timestamp: dayjs.Dayjs) => {
      const latestStart = effectiveVideoEnd.subtract(1, "second");
      const maxStart = latestStart.isBefore(effectiveVideoStartForWrites)
        ? effectiveVideoStartForWrites
        : latestStart;
      return timestamp.isBefore(effectiveVideoStartForWrites)
        ? effectiveVideoStartForWrites
        : timestamp.isAfter(maxStart)
          ? maxStart
          : timestamp;
    },
    [effectiveVideoEnd, effectiveVideoStartForWrites]
  );

  const createEvent = useCallback(
    (eventType: EventType, area: string | null, durationSeconds: number) => {
      if (activeRecordingRef.current || creationPendingRef.current) return null;
      if (!effectiveVideoEnd.isAfter(effectiveVideoStartForWrites)) return null;

      const currentTimestamp = videoStart.add(getLiveCurrentTime(), "second");
      const constrainedStart = constrainStart(currentTimestamp);
      const rawEnd = constrainedStart.add(durationSeconds, "second");
      const end = rawEnd.isAfter(effectiveVideoEnd)
        ? effectiveVideoEnd
        : rawEnd;
      const mappedMaterial =
        eventType === "lift" && area
          ? getAreaMaterialMappingAtTime(constrainedStart)[area]
          : null;
      const newEvent = defaultConstructor(
        withAreaLabels({
          id: uuidv4(),
          start: constrainedStart,
          end,
          eventType,
          material:
            mappedMaterial && mappedMaterial !== "unknown"
              ? mappedMaterial
              : null,
          pairId: null,
          area,
        })
      );

      creationPendingRef.current = true;
      setEvents((previous) => [...previous, newEvent]);
      return newEvent;
    },
    [
      effectiveVideoEnd,
      effectiveVideoStartForWrites,
      videoStart,
      getLiveCurrentTime,
      constrainStart,
      getAreaMaterialMappingAtTime,
      defaultConstructor,
      withAreaLabels,
      setEvents,
    ]
  );

  const handleAddFlag = useCallback(() => {
    createEvent("flag", null, 10);
  }, [createEvent]);

  const handleStartRecording = useCallback(
    (eventType: EventType, area?: string | null) => {
      const newEvent = createEvent(
        eventType,
        area ?? defaultAreas[eventType] ?? null,
        0
      );
      if (!newEvent) return;
      const preRecordingState: PreRecordingState = {
        wasPlaying: isPlaying,
        speed: playbackRate,
      };
      activeRecordingRef.current = { event: newEvent, preRecordingState };
      play();
      setPlaybackRate(
        Math.min(playbackRate, effectiveLabelRecordingPlaybackRate)
      );
      dispatch({ type: "START_RECORDING", event: newEvent });
    },
    [
      createEvent,
      defaultAreas,
      isPlaying,
      playbackRate,
      play,
      setPlaybackRate,
      effectiveLabelRecordingPlaybackRate,
    ]
  );

  const handleStopRecording = useCallback(
    // eventType, when given, stops only a recording of that type; omitted,
    // it stops whichever recording is currently active.
    (eventType?: EventType) => {
      const activeRecording = activeRecordingRef.current;
      if (!activeRecording) return;
      const recordingEvent =
        events.find((event) => event.id === activeRecording.event.id) ??
        activeRecording.event;
      if (eventType !== undefined && recordingEvent.eventType !== eventType) {
        return;
      }

      activeRecordingRef.current = null;
      const currentTimestamp = videoStart.add(getLiveCurrentTime(), "second");

      setEvents((prev) =>
        prev.map((event) => {
          if (event.id === recordingEvent.id) {
            const constrainedStart = constrainStart(event.start);

            const minEnd = constrainedStart.add(0.1, "second");
            const rawEnd = currentTimestamp.isAfter(minEnd)
              ? currentTimestamp
              : minEnd;
            const end = rawEnd.isAfter(effectiveVideoEnd)
              ? effectiveVideoEnd
              : rawEnd;

            let mass: string | null = null;
            let warnings: EventWarning[] | undefined = undefined;

            if (
              recordingEvent.eventType === "drop" &&
              extraData?.includes("mass_data") &&
              candidateEvents &&
              candidateEvents.length > 0 &&
              massEventMatching !== "on_lift"
            ) {
              const result = calculateMassAndWarnings(
                recordingEvent.area ?? null,
                constrainedStart,
                end,
                candidateEvents,
                massAreaMatching
              );
              mass = result.mass;
              warnings =
                result.warnings.length > 0 ? result.warnings : undefined;
            }

            return {
              ...event,
              start: constrainedStart,
              end,
              mass,
              warnings,
            };
          }
          return event;
        })
      );

      setPlaybackRate(activeRecording.preRecordingState.speed);
      if (activeRecording.preRecordingState.wasPlaying) {
        play();
      } else {
        pause();
      }

      dispatch({ type: "STOP_RECORDING" });
    },
    [
      getLiveCurrentTime,
      videoStart,
      effectiveVideoEnd,
      constrainStart,
      events,
      extraData,
      candidateEvents,
      setEvents,
      setPlaybackRate,
      play,
      pause,
      massAreaMatching,
      massEventMatching,
    ]
  );

  useEffect(() => {
    if (
      activeRecordingRef.current &&
      !events.some((event) => event.id === activeRecordingRef.current?.event.id)
    ) {
      handleStopRecording();
    }
  }, [events, handleStopRecording]);

  useEffect(() => {
    if (!state.recordingEventId) return;
    const intervalId = setInterval(() => {
      const rawEnd = videoStart.add(getLiveCurrentTime(), "second");
      if (!rawEnd.isBefore(effectiveVideoEnd)) {
        handleStopRecording();
        return;
      }
      setEvents((previous) =>
        previous.map((event) =>
          event.id === state.recordingEventId
            ? {
                ...event,
                end: rawEnd.isBefore(event.start) ? event.start : rawEnd,
              }
            : event
        )
      );
    }, 50);
    return () => {
      clearInterval(intervalId);
    };
  }, [
    state.recordingEventId,
    videoStart,
    getLiveCurrentTime,
    effectiveVideoEnd,
    handleStopRecording,
    setEvents,
  ]);

  const getConnectedEventIds = useCallback(
    (eventId: string): string[] =>
      findConnectedEventIds(eventsWithPairing, eventId),
    [findConnectedEventIds, eventsWithPairing]
  );

  const handleSetMaterial = useCallback(
    (material: string | null) => {
      const targetEventId = state.recordingEventId ?? state.selectedEventId;
      if (!targetEventId) return;
      const eventIdsToUpdate = [
        targetEventId,
        ...getConnectedEventIds(targetEventId),
      ];
      setEvents((prev) =>
        prev.map((event) =>
          eventIdsToUpdate.includes(event.id)
            ? { ...event, material, coMaterial: null }
            : event
        )
      );
    },
    [
      state.selectedEventId,
      state.recordingEventId,
      getConnectedEventIds,
      setEvents,
    ]
  );

  const handleSetArea = useCallback(
    (area: string | null) => {
      const targetEventId = state.recordingEventId ?? state.selectedEventId;
      if (!targetEventId) return;
      setEvents((prev) =>
        prev.map((event) =>
          event.id === targetEventId ? inArea(event, area) : event
        )
      );
    },
    [state.selectedEventId, state.recordingEventId, setEvents]
  );

  const handleUpdateEvent = useCallback(
    (
      id: string,
      updates: UpdateBody<T>,
      materialEditScope: MaterialEditScope = "withConnected"
    ): void => {
      const connectedEventIds =
        materialEditScope === "withConnected" ? getConnectedEventIds(id) : [];

      setEvents((prev) =>
        prev.map((event) => {
          if (event.id === id) {
            const updatedEvent = {
              ...("area" in updates
                ? inArea(event, updates.area ?? null)
                : event),
              ...updates,
            };

            if (
              "area" in updates &&
              updatedEvent.eventType === "drop" &&
              extraData?.includes("mass_data") &&
              candidateEvents &&
              candidateEvents.length > 0 &&
              massEventMatching !== "on_lift"
            ) {
              const result = calculateMassAndWarnings(
                updates.area ?? null,
                updatedEvent.start,
                updatedEvent.end,
                candidateEvents,
                massAreaMatching
              );

              return {
                ...updatedEvent,
                mass: result.mass,
                warnings:
                  result.warnings.length > 0 ? result.warnings : undefined,
              };
            }

            return updatedEvent;
          }
          if (connectedEventIds.includes(event.id)) {
            const materialUpdates: Partial<typeof event> = {};
            if ("material" in updates) {
              materialUpdates.material = updates.material;
            }
            if ("coMaterial" in updates) {
              materialUpdates.coMaterial = updates.coMaterial;
            }
            return { ...event, ...materialUpdates };
          }
          return event;
        })
      );
    },
    [
      setEvents,
      getConnectedEventIds,
      extraData,
      candidateEvents,
      massAreaMatching,
      massEventMatching,
    ]
  );

  const handleReverseEventType = useCallback(() => {
    if (!state.selectedEventId) return;

    const selectedEvent = eventsWithPairing.find(
      (e) => e.id === state.selectedEventId
    );
    if (!selectedEvent) return;

    if (selectedEvent.eventType === "flag") return;

    const eventIdsToUpdate = selectedEvent.pairId
      ? eventsWithPairing
          .filter((e) => e.pairId === selectedEvent.pairId)
          .map((e) => e.id)
      : [state.selectedEventId];

    setEvents((prev) =>
      prev.map((event) => {
        if (eventIdsToUpdate.includes(event.id)) {
          const toggledType: EventType =
            event.eventType === "lift" ? "drop" : "lift";
          return {
            ...event,
            eventType: toggledType,
          };
        }
        return event;
      })
    );
  }, [state.selectedEventId, eventsWithPairing, setEvents]);

  const handleSnapToCandidate = useCallback(() => {
    if (eventsWithPairing.length === 0 || !candidateEvents?.length) return;

    const currentTimestamp = videoStart.add(currentTime, "second");
    const eventsAtOrBeforeCursor = eventsWithPairing.filter(
      (e) => e.start.isSameOrBefore(currentTimestamp) && e.eventType === "drop"
    );

    if (eventsAtOrBeforeCursor.length === 0) return;

    const lastEvent = eventsAtOrBeforeCursor[eventsAtOrBeforeCursor.length - 1];
    if (!lastEvent || !lastEvent.area) return;
    const lastEventMiddle = lastEvent.start.add(
      lastEvent.end.diff(lastEvent.start, "second") / 2,
      "second"
    );

    const matchingCandidates = massAreaMatching
      ? candidateEvents.filter((candidate) => {
          const areaLabel = candidate.labels.find(
            (l) => l.label_type === "area"
          );
          return areaLabel?.label === lastEvent.area;
        })
      : candidateEvents;

    if (matchingCandidates.length === 0) return;

    const nearestCandidate = findNearestCandidate(
      matchingCandidates,
      lastEventMiddle,
      60
    );

    if (!nearestCandidate) return;

    const candidateStart = dayjs(nearestCandidate.event.start);
    const candidateEnd = dayjs(nearestCandidate.event.end);

    setEvents((prev) =>
      prev.map((event) => {
        if (event.id === lastEvent.id) {
          return {
            ...event,
            start: candidateStart,
            end: candidateEnd,
          };
        }
        return event;
      })
    );

    notifyStatus({
      type: "success",
      text: t("snappedToCandidate"),
      seconds: 2,
    });
  }, [
    eventsWithPairing,
    candidateEvents,
    currentTime,
    setEvents,
    videoStart,
    notifyStatus,
    t,
    massAreaMatching,
  ]);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, [setEvents]);

  return useMemo(
    () => ({
      events: eventsWithMassSuggestions,
      editableEvents: events,
      selectedEventId: state.selectedEventId,
      recordingEventId: state.recordingEventId,
      handleSelectEvent,
      setSelectedEventId,
      handleDeleteEvent,
      handleStartRecording,
      handleStopRecording,
      handleSetMaterial,
      handleSetArea,
      handleUpdateEvent,
      getConnectedEventIds,
      handleReverseEventType,
      handleAddFlag,
      handleSnapToCandidate,
      setEvents,
      clearEvents,
      areaMaterialSnapshots,
      getAreaMaterialMappingAtTime,
      getAreaMaterialMappingBeforeTime,
      updateAreaMaterialMapping,
      deleteAreaMaterialSnapshot,
      isUsingPersistentDefaults,
      commitPersistentMapping,
      defaultAreaMaterials: defaultAreaMaterials ?? {},
    }),
    [
      eventsWithMassSuggestions,
      events,
      state.selectedEventId,
      state.recordingEventId,
      handleSelectEvent,
      setSelectedEventId,
      handleDeleteEvent,
      handleStartRecording,
      handleStopRecording,
      handleSetMaterial,
      handleSetArea,
      handleUpdateEvent,
      getConnectedEventIds,
      handleReverseEventType,
      handleAddFlag,
      handleSnapToCandidate,
      setEvents,
      clearEvents,
      areaMaterialSnapshots,
      getAreaMaterialMappingAtTime,
      getAreaMaterialMappingBeforeTime,
      updateAreaMaterialMapping,
      deleteAreaMaterialSnapshot,
      isUsingPersistentDefaults,
      commitPersistentMapping,
      defaultAreaMaterials,
    ]
  );
};
